
import React from 'react';
import { useLanguage } from './LanguageContext';
import { PAGE_CONTENT, ICON_MAP } from '../constants';
import { ViewType } from '../types';
import { ArrowLeft } from 'lucide-react';

interface StaticPageProps {
  view: ViewType;
  onBack: () => void;
}

const StaticPage: React.FC<StaticPageProps> = ({ view, onBack }) => {
  const { language } = useLanguage();
  const content = PAGE_CONTENT[language]?.[view] || PAGE_CONTENT['EN'][view];

  if (!content) return null;

  return (
    <section className="py-24 bg-white animate-fade-in">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-[#c5a059] font-bold text-xs tracking-widest uppercase mb-12 hover:-translate-x-2 transition-transform duration-300"
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'EN' ? 'Back to Home' : 'Kthehu tek Kreu'}
        </button>

        <div className="text-center mb-20">
          <h1 className="text-4xl md:text-6xl font-serif font-bold mb-6">{content.title}</h1>
          <div className="w-24 h-1.5 bg-[#c5a059] mx-auto mb-8"></div>
          <p className="text-gray-600 max-w-3xl mx-auto text-xl font-light leading-relaxed">
            {content.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {content.cards.map((card, idx) => (
            <div 
              key={idx}
              className="bg-[#fafafa] p-10 border border-gray-100 transition-all duration-500 hover:bg-white hover:shadow-2xl hover:-translate-y-2 flex flex-col items-center text-center group"
            >
              {card.icon && ICON_MAP[card.icon] && (
                <div className="text-[#c5a059] mb-8 bg-[#c5a059]/5 p-4 rounded-full group-hover:bg-[#c5a059]/10 transition-colors">
                  {ICON_MAP[card.icon]}
                </div>
              )}
              {card.meta && (
                <span className="text-[10px] text-[#c5a059] font-bold tracking-[0.3em] uppercase mb-4 block">
                  {card.meta}
                </span>
              )}
              <h3 className="text-2xl font-bold mb-5 font-serif">{card.title}</h3>
              <p className="text-gray-500 leading-relaxed font-light">
                {card.description}
              </p>
              
              <div className="absolute bottom-0 left-0 w-full h-1 bg-[#c5a059] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-center"></div>
            </div>
          ))}
        </div>

        {/* Dynamic CTA at the bottom of static pages */}
        <div className="mt-24 p-12 bg-[#0a0a0a] text-white text-center rounded-sm shadow-2xl overflow-hidden relative">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#c5a059]/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"></div>
          <h2 className="text-3xl font-serif font-bold mb-6 relative z-10">
            {language === 'EN' ? 'Ready to discuss your case?' : 'Gati për të diskutuar çështjen tuaj?'}
          </h2>
          <button 
            onClick={() => { onBack(); setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
            className="relative z-10 bg-[#c5a059] text-white px-10 py-5 font-bold text-xs tracking-[0.2em] uppercase hover:bg-[#b08b49] transition-all duration-300"
          >
            {language === 'EN' ? 'Get Started' : 'Fillo Tani'}
          </button>
        </div>
      </div>
    </section>
  );
};

export default StaticPage;
